import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  Chip,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Launch, Chat, Visibility, Code } from '@mui/icons-material';

const Examples = () => {
  // Real example - our Discord chat iframe
  const example = {
    id: 1,
    name: 'Hitbit - using wizcord',
    testUrl: '/iframe/test',
    iframeUrl: 'https://ai-bet.online/iframe?channelId=YOUR_CHANNEL_ID',
         screenshot: '/images/hitbit-screenshot.jpg',
    description: 'Live Discord chat embedded directly on a website. Users can chat as guests or login with Discord accounts. Features real-time messaging, channel switching, and responsive design.',
    category: 'Live Example',
    features: ['Real-time Chat', 'Discord Authentication', 'Guest Mode', 'Channel Sidebar', 'Mobile Responsive', 'WebSocket Updates']
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ pt: 8, pb: 6, textAlign: 'center' }}>
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #10b981 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            🌟 Live Example
          </Typography>
          <Typography
            variant="h5"
            sx={{ 
              color: 'text.secondary', 
              mb: 4, 
              maxWidth: 800, 
              mx: 'auto', 
              lineHeight: 1.6 
            }}
          >
            See Wizcord in action with some of our users
          </Typography>
        </Box>

        {/* Example Card */}
                 <Grid container justifyContent="center" sx={{ mb: 8 }}>
           <Grid item xs={12} md={10} lg={8}>
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                  borderColor: 'rgba(99, 102, 241, 0.3)',
                },
              }}
            >
              <CardMedia
                component="img"
                height="500"
                image={example.screenshot}
                alt={`${example.name} screenshot`}
                sx={{
                  objectFit: 'contain',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  backgroundColor: '#1e293b',
                }}
              />
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {example.name}
                  </Typography>
                  <Chip
                    label={example.category}
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      px: 2,
                      py: 1,
                    }}
                  />
                </Box>
                
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, lineHeight: 1.7, fontSize: '1.1rem' }}
                >
                  {example.description}
                </Typography>

                {/* Features */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Features:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {example.features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        size="medium"
                        variant="outlined"
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                                 {/* Action Buttons */}
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                   <Button
                     href="https://hitbit.ai/fc25"
                     target="_blank"
                     rel="noopener noreferrer"
                     variant="contained"
                     size="large"
                     startIcon={<Launch />}
                     sx={{
                       fontSize: '1rem',
                       py: 1.5,
                       boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                     }}
                   >
                     Visit Hitbit
                   </Button>

                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                                         <Button
                       component={Link}
                       to="/docs"
                       replace
                       onClick={() => window.scrollTo(0, 0)}
                       variant="outlined"
                       size="medium"
                       startIcon={<Code />}
                       sx={{
                         flex: 1,
                         borderColor: 'primary.main',
                         color: 'primary.main',
                         '&:hover': {
                           borderColor: 'primary.dark',
                           backgroundColor: 'rgba(99, 102, 241, 0.1)',
                         },
                       }}
                     >
                       View Docs
                     </Button>
                    <Button
                      component={Link}
                      to="/register"
                      variant="outlined"
                      size="medium"
                      startIcon={<Chat />}
                      sx={{
                        flex: 1,
                        borderColor: 'success.main',
                        color: 'success.main',
                        '&:hover': {
                          borderColor: 'success.dark',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        },
                      }}
                    >
                      Get Started
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* How it Works Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 6,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            How It Works
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.7) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Typography variant="h3" sx={{ mb: 2 }}>1️⃣</Typography>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Get Discord IDs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Copy your Discord channel ID and optionally your server ID for channel switching
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.7) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Typography variant="h3" sx={{ mb: 2 }}>2️⃣</Typography>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Add iframe Code
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Embed our iframe on your website with your Discord channel configuration
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.7) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Typography variant="h3" sx={{ mb: 2 }}>3️⃣</Typography>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Start Chatting
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your visitors can immediately start chatting with your Discord community
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box sx={{ textAlign: 'center', pb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Ready to Add Discord Chat to Your Site?
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
          >
            Join thousands of websites using Wizcord to build engaged communities with Discord integration.
          </Typography>
          <Button
            component={Link}
            to="/register"
            variant="contained"
            size="large"
            startIcon={<Chat />}
            sx={{
              fontSize: '1.1rem',
              px: 4,
              py: 1.5,
              mr: 2,
              mb: 2,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            }}
          >
            Get Started Free
          </Button>
          <Button
            component={Link}
            to="/docs"
            replace
            onClick={() => window.scrollTo(0, 0)}
            variant="outlined"
            size="large"
            startIcon={<Visibility />}
            sx={{
              fontSize: '1.1rem',
              px: 4,
              py: 1.5,
              mb: 2,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
              },
            }}
          >
            View Documentation
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Examples; 